// src/App.js - Updated with MillMind landing page and code-splitting
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { ProtectedRoute } from './middleware/auth';
import theme from './theme';
import { App as CapacitorApp } from '@capacitor/app';

import './App.css';
import { NotificationProvider } from './pages/shared/hooks/notification';
import { UserProvider } from './pages/shared/context/userContext';
import Production from './features/production-planning/prodution';
import CurtainRaiser from './components/CurtainRaiser';

// Lazy-loaded components for code splitting (reduces initial bundle size)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const HomePage = lazy(() => import('./pages/HomePage'));
const AdminPage = lazy(() => import('./pages/adminpage'));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard'));
const JMCDashboard = lazy(() => import('./pages/JMCDashboard'));
const DailyReportPage = lazy(() => import('./pages/DailyReportPage'));
const Consumption = lazy(() => import('./features/consuption/components/consumption'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MonthlyCostsPage = lazy(() => import('./pages/MonthlyCostsPage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/AnalyticsDashboardPage'));
const AdminAnalyticsDashboard = lazy(() => import('./pages/AdminAnalyticsDashboard'));
const PulpMillDashboard = lazy(() => import('./pages/PulpMillDashboard'));
const PaperMillDashboard = lazy(() => import('./pages/PaperMillDashboard'));
const MaintenanceDashboardPage = lazy(() => import('./pages/MaintenanceDashboardPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

// Back button handler component (must be inside Router)
const BackButtonHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleBackButton = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else if (location.pathname !== '/' && location.pathname !== '/home') {
        navigate('/home');
      }
    });

    return () => {
      handleBackButton.then(listener => listener.remove());
    };
  }, [navigate, location]);

  return null;
};

// CURTAIN RAISER: Set to true to enable, false to disable completely
const CURTAIN_ALWAYS_ON = false;

function App() {
  const [showCurtain, setShowCurtain] = useState(() => {
    // Curtain raiser is disabled - set CURTAIN_ALWAYS_ON to true to re-enable
    if (!CURTAIN_ALWAYS_ON) {
      return false;
    }

    // Check URL params for curtain control
    const urlParams = new URLSearchParams(window.location.search);

    // ?skipCurtain=true - Force skip curtain for this session
    if (urlParams.get('skipCurtain') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      return false;
    }

    // ?noCurtain=true - Permanently skip curtain (sets the flag)
    if (urlParams.get('noCurtain') === 'true') {
      localStorage.setItem('curtainShown', 'true');
      window.history.replaceState({}, '', window.location.pathname);
      return false;
    }

    // Show curtain on every visit when enabled
    return true;
  });

  const handleCurtainComplete = () => {
    setShowCurtain(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {showCurtain && <CurtainRaiser onComplete={handleCurtainComplete} />}
      <NotificationProvider>
        <UserProvider>
          <Router>
            <BackButtonHandler />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Protected routes */}
                <Route path="/home" element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                } />

                {/* Legacy routes for backward compatibility */}
                <Route path="/chat" element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                } />

                <Route path="/qa" element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                } />

                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/roles" element={
                  <ProtectedRoute roles={["admin"]}>
                    <RoleManagementPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/analytics" element={
                  <ProtectedRoute roles={["admin"]}>
                    <AdminAnalyticsDashboard />
                  </ProtectedRoute>
                } />

                {/* <Route path="/operations" element={
                  <ProtectedRoute>
                    <OperationsDashboard />
                  </ProtectedRoute>
                } /> */}
                <Route path="/production" element={
                  <ProtectedRoute>
                    <Production />
                  </ProtectedRoute>
                } />

                <Route path="/jmc-dashboard" element={
                  <ProtectedRoute roles={["admin"]} >
                    <JMCDashboard />
                  </ProtectedRoute>
                } />

                <Route path="/consumption" element={
                  <ProtectedRoute roles={["user", "manager", "admin"]} >
                    <Consumption />
                  </ProtectedRoute>
                } />

                <Route path="/daily-report" element={
                  <ProtectedRoute>
                    <DailyReportPage />
                  </ProtectedRoute>
                } />

                <Route path="/production-dispatch" element={
                  <ProtectedRoute>
                    <Production />
                  </ProtectedRoute>
                } />

                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />

                <Route path="/monthly-costs" element={
                  <ProtectedRoute roles={["admin", "manager"]}>
                    <MonthlyCostsPage />
                  </ProtectedRoute>
                } />

                <Route path="/analytics-dashboard" element={
                  <ProtectedRoute roles={["admin", "manager"]}>
                    <AnalyticsDashboardPage />
                  </ProtectedRoute>
                } />

                <Route path="/pulp-mill-dashboard" element={
                  <ProtectedRoute>
                    <PulpMillDashboard />
                    </ProtectedRoute>
                } />

                <Route path="/paper-mill-dashboard" element={
                  <ProtectedRoute>
                    <PaperMillDashboard />
                  </ProtectedRoute>
                } />

                <Route path="/maintenance-dashboard" element={
                  <ProtectedRoute>
                    <MaintenanceDashboardPage />
                  </ProtectedRoute>
                } />

                {/* JMC Dashboard V1 - Hidden from frontend (preparing for V2) */}
                {/* <Route path="/jmc-dashboard-v1" element={
                  <ProtectedRoute>
                    <JMCDashboardV1 />
                  </ProtectedRoute>
                } /> */}
              </Routes>
            </Suspense>
          </Router>
        </UserProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
